import axios from "axios";

import { panelApiUrl } from "~/Utils";

/**
 * Is the signed-in user allowed into each module?
 *
 * `inactivePanelUser` / `inactiveSpaUser` are written once, during login, and
 * never revisited. Activating someone in Panel Contract after they signed in
 * therefore left them stuck on "De-activated User" until they logged out and
 * back in.
 *
 * The flag is re-read from `/user/user-access` at the moment of navigating, so
 * a change made since login is honoured. The stored flag is the fallback: if
 * the check cannot be made, the user is only turned away when login positively
 * recorded them as inactive, never merely because the call failed.
 */

/**
 * Login stores these via useLocalStorage, so the value is JSON-encoded.
 *
 * Only a real boolean counts as an answer. `useLocalStorage` writes its
 * default — the STRING "false" — into the key the first time it mounts, before
 * anyone has signed in, so reading that string as "inactive" turned away every
 * user whose flag the API had not yet recorded.
 */
const storedFlag = (key: string): boolean | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const value = JSON.parse(raw);
    return typeof value === "boolean" ? value : null;
  } catch {
    return null;
  }
};

const authHeaders = () => {
  try {
    const user = JSON.parse(window.localStorage.getItem("user") || "{}");
    const provider = JSON.parse(window.localStorage.getItem("authProvider") || '"Internal"');
    return {
      ...(user?.token && { Authorization: `Bearer ${user.token}` }),
      "X-Auth-Provider": provider || "Internal",
    };
  } catch {
    return {};
  }
};

/**
 * Every role Panel Contract's routes accept — mirrors EVERY_PANEL_ROLE in
 * PanelContract/Routes/Routes.tsx. Kept here rather than imported so the module
 * picker does not pull the whole panel route table in behind it.
 */
const PANEL_ROLES = [
  "PANEL_ADMIN",
  "PANEL_MASTER_ADMIN",
  "PANEL_REVIEWER",
  "PANEL_MASTER_REVIEWER",
  "PANEL_CREATOR",
];

const isPanelRole = (role: any): boolean => PANEL_ROLES.includes(String(role ?? "").trim());

/** The role login recorded, for when the live check cannot be made. */
const storedPanelRole = (): string => {
  try {
    return JSON.parse(window.localStorage.getItem("panelUser") || "{}")?.currentRole ?? "";
  } catch {
    return "";
  }
};

/** Accepts a boolean or the string the API sometimes sends in its place. */
const asBoolean = (value: any): boolean | null => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
};

/**
 * May this user enter Panel Contract?
 *
 * A panel role decides it; `isActivePanelUser` decides only for someone the
 * endpoint gives no role. Both are read live at the moment of navigating. What broke
 * this before was not the rule but the fallback: login writes the flag through
 * `useLocalStorage`, whose DEFAULT is the string "false", and reading that
 * string as `false` turned away every user whose flag had not been recorded
 * yet. Only a real boolean counts (see `storedFlag`).
 */
export const isPanelUserActive = async (): Promise<boolean> => {
  try {
    const { data }: any = await axios.get(`${panelApiUrl}/user/user-access`, {
      headers: authHeaders(),
    });
    const access = data?.response?.data;
    /* Panel's own document key, kept fresh on every entry to Panel Contract —
       not only at sign-in — so stored Panel files open without signing in again. */
    if (access?.docVersionKey) localStorage.setItem("panelDocVersionKey", JSON.stringify(access.docVersionKey));

    /* A panel role is the grant. The endpoint can answer with a role and
       `isActivePanelUser: false` in the same breath — PANEL_CREATOR alongside
       "not an active panel user" — and reading the flag alone turned such a
       user away at the door, even though every route inside would have let
       their role in. The flag now decides only for someone with no role. */
    if (isPanelRole(access?.currentRole)) return true;

    const isActive = asBoolean(access?.isActivePanelUser);
    if (isActive !== null) return isActive;
  } catch {
    // fall through to what login recorded
  }
  /* Same rule against what login stored: a recorded panel role is enough. */
  if (isPanelRole(storedPanelRole())) return true;
  return storedFlag("inactivePanelUser") !== false;
};

/**
 * Where entering Panel Contract lands.
 *
 * Everyone used to be sent to Work Order User Management, which is guarded for
 * PANEL_ADMIN alone — so a Work Order Approver or Creator entering the module
 * was bounced straight to the 403 page and never saw the sidebar at all. Only
 * an admin starts there now; everyone else starts on Work Order (the dashboard is hidden for now).
 */
export const panelLandingRoute = (): string => {
  try {
    const panelUser = JSON.parse(window.localStorage.getItem("panelUser") || "{}");
    if (panelUser?.currentRole === "PANEL_ADMIN") {
      return "/workorder-user-management?activeTab=activeUsers";
    }
  } catch {
    // fall through to the route every panel role may open
  }
  return "/workorder";
};
